import _extends from "/opt/build/repo/node_modules/.pnpm/next@15.5.6_@babel+core@7.28.5_@opentelemetry+api@1.9.0_@playwright+test@1.56.1_babel-p_096ab9bbeffdff0fc88514782f3793fe/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/extends.js";
import _objectWithoutPropertiesLoose from "/opt/build/repo/node_modules/.pnpm/next@15.5.6_@babel+core@7.28.5_@opentelemetry+api@1.9.0_@playwright+test@1.56.1_babel-p_096ab9bbeffdff0fc88514782f3793fe/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/objectWithoutPropertiesLoose.js";
import * as React from 'react';
import { useRouter } from 'next/router';
import { deepmerge } from '@mui/utils';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { unstable_useEnhancedEffect as useEnhancedEffect } from '@mui/material/utils';
import { colorChannel, getContrastRatio, lighten, darken } from '@mui/system/colorManipulator';
import CssBaseline from '@mui/material/CssBaseline';
import { getCookie, pathnameToLanguage } from 'docs/src/modules/utils/helpers';
// @ts-ignore to bypass type checking in MUI X repo
import { NextNProgressBar } from 'docs/src/modules/components/AppFrame';
import { getDesignTokens, getThemedComponents } from '@mui/docs/branding';
import SkipLink from 'docs/src/modules/components/SkipLink';
// @ts-ignore to bypass type checking in MUI X repo
import MarkdownLinks from 'docs/src/modules/components/MarkdownLinks';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
var _getDesignTokens = getDesignTokens('light'),
  lightPalette = _getDesignTokens.palette,
  typography = _getDesignTokens.typography,
  designTokens = _objectWithoutPropertiesLoose(_getDesignTokens, ["palette", "typography"]);
var _getDesignTokens2 = getDesignTokens('dark'),
  darkPalette = _getDesignTokens2.palette;
var themeOptions = _extends({
  colorSchemes: {
    light: {
      palette: lightPalette
    },
    dark: {
      palette: darkPalette
    }
  }
}, designTokens, {
  typography: deepmerge(typography, {
    h1: {
      ':where([data-mui-color-scheme="dark"]) &': {
        color: 'var(--muidocs-palette-common-white)'
      }
    },
    h2: {
      ':where([data-mui-color-scheme="dark"]) &': {
        color: 'var(--muidocs-palette-grey-100)'
      }
    },
    h5: {
      ':where([data-mui-color-scheme="dark"]) &': {
        color: 'var(--muidocs-palette-primary-300)'
      }
    }
  })
}, getThemedComponents());
export function setDocsColors(primary, secondary) {
  function injectPalette(prefix, palette, color) {
    // simplified logic of `createPalette` to avoid `useTheme`.
    var light = lighten(color, 0.2);
    var dark = darken(color, 0.3);
    var contrastText = getContrastRatio(color, '#fff') >= 3 ? '#fff' : 'rgba(0, 0, 0, 0.87)';
    document.documentElement.style.setProperty("--".concat(prefix, "-palette-").concat(palette, "-main"), color);
    document.documentElement.style.setProperty("--".concat(prefix, "-palette-").concat(palette, "-mainChannel"), colorChannel(color));
    document.documentElement.style.setProperty("--".concat(prefix, "-palette-").concat(palette, "-light"), light);
    document.documentElement.style.setProperty("--".concat(prefix, "-palette-").concat(palette, "-lightChannel"), colorChannel(light));
    document.documentElement.style.setProperty("--".concat(prefix, "-palette-").concat(palette, "-dark"), dark);
    document.documentElement.style.setProperty("--".concat(prefix, "-palette-").concat(palette, "-darkChannel"), colorChannel(dark));
    document.documentElement.style.setProperty("--".concat(prefix, "-palette-").concat(palette, "-contrastText"), contrastText);
    document.documentElement.style.setProperty("--".concat(prefix, "-palette-").concat(palette, "-contrastTextChannel"), colorChannel(contrastText));
  }
  if (typeof document !== 'undefined') {
    injectPalette('muidocs', 'primary', primary.main);
    injectPalette('muidocs', 'secondary', secondary.main);
    ['50', '100', '200', '300', '400', '500', '600', '700', '800', '900'].forEach(function (key) {
      document.documentElement.style.setProperty("--muidocs-palette-primary-".concat(key), primary[key]);
      document.documentElement.style.setProperty("--muidocs-palette-secondary-".concat(key), secondary[key]);
    });
    injectPalette('mui', 'primary', primary.main);
    injectPalette('mui', 'secondary', secondary.main);
  }
}
export function resetDocsColor() {
  if (typeof document !== 'undefined') {
    document.documentElement.style.removeProperty('--muidocs-palette-primary-main');
    document.documentElement.style.removeProperty('--muidocs-palette-secondary-main');
    document.documentElement.style.removeProperty('--mui-palette-primary-main');
    document.documentElement.style.removeProperty('--mui-palette-secondary-main');
    ['50', '100', '200', '300', '400', '500', '600', '700', '800', '900'].forEach(function (key) {
      document.documentElement.style.removeProperty("--muidocs-palette-primary-".concat(key));
      document.documentElement.style.removeProperty("--muidocs-palette-secondary-".concat(key));
    });
  }
}
export function setDocsSpacing(value) {
  if (typeof document !== 'undefined') {
    document.documentElement.style.setProperty('--muidocs-spacing', "".concat(value, "px"));
    document.documentElement.style.setProperty('--mui-spacing', "".concat(value, "px"));
  }
}
export function resetDocsSpacing() {
  if (typeof document !== 'undefined') {
    document.documentElement.style.removeProperty('--muidocs-spacing');
    document.documentElement.style.removeProperty('--mui-spacing');
  }
}
export default function BrandingCssVarsProvider(props) {
  var _props$direction = props.direction,
    direction = _props$direction === void 0 ? 'ltr' : _props$direction,
    children = props.children;
  var _useRouter = useRouter(),
    asPath = _useRouter.asPath;
  var _pathnameToLanguage = pathnameToLanguage(asPath),
    canonicalAs = _pathnameToLanguage.canonicalAs;
  var theme = React.useMemo(function () {
    return createTheme(_extends({
      cssVariables: {
        cssVarPrefix: 'muidocs',
        colorSchemeSelector: 'data-mui-color-scheme'
      },
      direction: direction
    }, themeOptions));
  }, [direction]);
  useEnhancedEffect(function () {
    var nextPaletteColors = JSON.parse(getCookie('paletteColors') || 'null');
    if (nextPaletteColors) {
      setDocsColors(nextPaletteColors.primary, nextPaletteColors.secondary);
    }
  }, []);
  useEnhancedEffect(function () {
    // This is required to ensure that the layer order is declared first in the head
    // because when the direction is RTL on the client, emotion reinserts the RTL styles back to the top of the insertion point.
    if (direction === 'rtl') {
      var head = document.querySelector('head');
      if (head) {
        var style = document.createElement('style');
        style.textContent = '@layer theme, docsearch, mui, mui.global, mui.default, mui.theme, mui.custom, mui.sx, utilities;';
        head.prepend(style);
      }
    }
  }, [direction]);
  return /*#__PURE__*/_jsxs(ThemeProvider, {
    theme: theme,
    disableTransitionOnChange: true
    // TODO: remove `forceThemeRerender` once custom theme on some demos rely on CSS variables instead of `theme.palette.mode`
    ,
    forceThemeRerender: canonicalAs.startsWith('/x/') || canonicalAs.startsWith('/toolpad/'),
    children: [/*#__PURE__*/_jsx(NextNProgressBar, {}), /*#__PURE__*/_jsx(CssBaseline, {}), /*#__PURE__*/_jsx(SkipLink, {}), /*#__PURE__*/_jsx(MarkdownLinks, {}), children]
  });
}